import React, { memo } from 'react';
import TextFieldWithDebounce from 'ui/WithDebounce/TextFieldWithDebounce';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { startAdornment } from 'ui/AgGridReact/CurrencyInputAdornment';
import { useScrollessRef } from 'hooks/helpers';
import AmountFormat from 'components/NumberFormat/AmountFormat';
import {
    getNewFormattedValue,
    getVentilationFromNode,
} from '../helpers/dataHelpers';
import { UpdateProjectionDetailsParams } from '../helpers/colDefinitions';
import { useUpdatePluriannualProjectionDetails } from '../hooks/pluriannual';
import { StyProjectionDetailsCellRenderer } from '../helpers/styles';
import {
    PluriannualProjectionDetails,
    ProjectionsDimensionsMapping,
} from '../helpers/types';
import { ProjectionTooltip } from './ProjectionToolTip';
import { TextFieldProps } from '@mui/material';

export type PluriannualProjectionDetailsCellRendererCustomParams = {
    resourceSoftwareId: string | undefined;
    pluriannualProjectionDetailsUpdateTracker: () => void;
    dimensionsMapping: ProjectionsDimensionsMapping;
    projectionIdLabelMapping: Record<string, string>;
};

type PluriannualProjectionDetailsCellRendererParams =
    CellRendererParams<PluriannualProjectionDetails.WithMetadata> & {
        value?: number;
        year: number;
        updateProjectionParams: UpdateProjectionDetailsParams;
        customParams: PluriannualProjectionDetailsCellRendererCustomParams;
        textFieldProps: TextFieldProps;
        defaultValue?: number;
    };

function PluriannualProjectionDetailCellRenderer({
    value,
    node,
    year,
    defaultValue,
    textFieldProps,
    customParams,
    updateProjectionParams,
}: PluriannualProjectionDetailsCellRendererParams): React.ReactElement {
    // We need this ref to disable input changing with mouse wheel
    const ref = useScrollessRef();
    const { budgetProposal, expenseOrIncome, isDaf } = updateProjectionParams;
    const { resourceSoftwareId, pluriannualProjectionDetailsUpdateTracker } =
        customParams;
    const dimensions = getVentilationFromNode(node);

    const updateProjectionMutation = useUpdatePluriannualProjectionDetails({
        budgetProposal,
        expenseOrIncome,
        isDaf,
        dimensions,
    });

    if (value === undefined) {
        return <span />;
    }

    if (!node.leafGroup || node.footer) {
        return (
            <AmountFormat
                value={value}
                defaultValue={defaultValue}
                fixedDecimalScale
            />
        );
    }

    const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const finalValue = getNewFormattedValue(
            event.target.value,
            defaultValue,
            'amount',
        );
        pluriannualProjectionDetailsUpdateTracker();
        updateProjectionMutation.mutate({
            value: finalValue,
            year,
            resourceSoftwareId,
        });
    };

    const hasNoBudgetLine = value === null;
    const canEditPluriannualProjections =
        budgetProposal.canEditPluriannualProjections ?? true;
    const canEdit = node?.allLeafChildren?.[0]?.data?.canEdit ?? false;

    return (
        <ProjectionTooltip
            disabled={hasNoBudgetLine}
            disabledByResource={!canEdit}
            disabledByBudgetProposal={!canEditPluriannualProjections}
            by={budgetProposal.by}
        >
            <StyProjectionDetailsCellRenderer>
                <TextFieldWithDebounce
                    className="mui-text-field-input"
                    value={value || defaultValue}
                    disabled={
                        !canEdit ||
                        hasNoBudgetLine ||
                        !canEditPluriannualProjections
                    }
                    fullWidth
                    variant="outlined"
                    size="small"
                    onChange={onValueChange}
                    InputProps={{
                        disableInjectingGlobalStyles: true,
                        startAdornment,
                    }}
                    {...textFieldProps}
                    ref={ref}
                />
            </StyProjectionDetailsCellRenderer>
        </ProjectionTooltip>
    );
}

export default memo(PluriannualProjectionDetailCellRenderer);
